<template>
    <div class="container-fluid">
        <div class="page-title">
            <div class="row">
                <div class="col-6">
                    <h3>KYT Logs</h3>
                </div>
                <div class="col-6">
                    <ol class="breadcrumb">
                        <li class="breadcrumb-item">
                            <router-link to="/">Home</router-link>
                        </li>
                        <li class="breadcrumb-item active">KYT Logs</li>
                    </ol>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-4">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <!-- Error State -->
        <div v-if="error" class="alert alert-danger" role="alert">
            <i class="fa fa-exclamation-triangle me-2"></i>
            {{ error }}
            <button type="button" class="btn btn-sm btn-outline-danger ms-2" @click="() => fetchMainKyts()">
                <i class="fa fa-refresh me-1"></i>
                Retry
            </button>
        </div>

        <!-- Main Content -->
        <div v-if="!loading && !error" class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-header">
                        <div class="row align-items-center">
                            <div class="col">
                                <h5 class="mb-0">Main KYT Logs</h5>
                                <small class="text-muted">
                                    Total: {{ mainKytsData?.summary?.totalMainKyts || 0 }} records
                                </small>
                            </div>
                            <div class="col-auto">
                                <button 
                                    type="button" 
                                    class="btn btn-primary btn-sm" 
                                    @click="() => fetchMainKyts()"
                                    :disabled="loading"
                                >
                                    <i class="fa fa-refresh me-1"></i>
                                    Refresh
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Filters Section -->
                    <div class="card-body border-bottom bg-light" v-show="showFilters">
                        <div class="row g-3">
                            <div class="col-md-3">
                                <label for="siteName" class="form-label small fw-medium">Site Name</label>
                                <input 
                                    type="text" 
                                    class="form-control form-control-sm" 
                                    id="siteName"
                                    v-model="filters.siteName"
                                    placeholder="Search by site name..."
                                    @input="debouncedSearch"
                                />
                            </div>
                            <div class="col-md-3">
                                <label for="empName" class="form-label small fw-medium">Employee Name</label>
                                <input 
                                    type="text" 
                                    class="form-control form-control-sm" 
                                    id="empName"
                                    v-model="filters.empName"
                                    placeholder="Search by employee name..."
                                    @input="debouncedSearch"
                                />
                            </div>
                            <div class="col-md-3">
                                <label for="status" class="form-label small fw-medium">Status</label>
                                <select 
                                    class="form-select form-select-sm" 
                                    id="status"
                                    v-model="filters.status"
                                    @change="applyFilters"
                                >
                                    <option value="">All Status</option>
                                    <option value="open">Open</option>
                                    <option value="approved">Approved</option>
                                    <option value="completed">Completed</option>
                                    <option value="pending">Pending</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="cancelled">Cancelled</option>
                                    <option value="closed">Closed</option>
                                </select>
                            </div>
                            <div class="col-md-3">
                                <label for="sortBy" class="form-label small fw-medium">Sort By</label>
                                <div class="input-group input-group-sm">
                                    <select 
                                        class="form-select" 
                                        id="sortBy"
                                        v-model="filters.sortBy"
                                        @change="applyFilters"
                                    >
                                        <option value="">Default</option>
                                        <option value="siteName">Site Name</option>
                                        <option value="nfd">NFD Date</option>
                                        <option value="createdAt">Created Date</option>
                                        <option value="status">Status</option>
                                    </select>
                                    <button 
                                        class="btn btn-outline-secondary" 
                                        type="button"
                                        @click="toggleSortOrder"
                                        :title="filters.sortOrder === 'asc' ? 'Sort Ascending' : 'Sort Descending'"
                                    >
                                        <i class="fa" :class="filters.sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Date Range Filters -->
                        <div class="row g-3 mt-2">
                            <div class="col-md-3">
                                <label for="nfdFrom" class="form-label small fw-medium">NFD From</label>
                                <input 
                                    type="date" 
                                    class="form-control form-control-sm" 
                                    id="nfdFrom"
                                    v-model="filters.nfdFrom"
                                    @change="applyFilters"
                                />
                            </div>
                            <div class="col-md-3">
                                <label for="nfdTo" class="form-label small fw-medium">NFD To</label>
                                <input 
                                    type="date" 
                                    class="form-control form-control-sm" 
                                    id="nfdTo"
                                    v-model="filters.nfdTo"
                                    @change="applyFilters"
                                />
                            </div>
                            <div class="col-md-3">
                                <label for="dateFrom" class="form-label small fw-medium">Created From</label>
                                <input 
                                    type="date" 
                                    class="form-control form-control-sm" 
                                    id="dateFrom"
                                    v-model="filters.dateFrom"
                                    @change="applyFilters"
                                />
                            </div>
                            <div class="col-md-3 d-flex align-items-end">
                                <div class="w-100">
                                    <button 
                                        type="button" 
                                        class="btn btn-outline-secondary btn-sm me-2" 
                                        @click="clearFilters"
                                    >
                                        <i class="fa fa-times me-1"></i>
                                        Clear
                                    </button>
                                    <button 
                                        type="button" 
                                        class="btn btn-outline-info btn-sm" 
                                        @click="toggleFilters"
                                    >
                                        <i class="fa" :class="showFilters ? 'fa-eye-slash' : 'fa-eye'"></i>
                                        {{ showFilters ? 'Hide' : 'Show' }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Active Filters Display -->
                        <div v-if="hasActiveFilters" class="mt-3">
                            <div class="d-flex flex-wrap gap-2">
                                <span class="small text-muted me-2">Active filters:</span>
                                <span v-if="filters.siteName" class="badge bg-primary">
                                    Site: {{ filters.siteName }}
                                    <button class="btn-close btn-close-white ms-1" @click="clearFilter('siteName')"></button>
                                </span>
                                <span v-if="filters.empName" class="badge bg-primary">
                                    Employee: {{ filters.empName }}
                                    <button class="btn-close btn-close-white ms-1" @click="clearFilter('empName')"></button>
                                </span>
                                <span v-if="filters.status" class="badge bg-primary">
                                    Status: {{ filters.status }}
                                    <button class="btn-close btn-close-white ms-1" @click="clearFilter('status')"></button>
                                </span>
                                <span v-if="filters.nfdFrom" class="badge bg-info">
                                    NFD From: {{ filters.nfdFrom }}
                                    <button class="btn-close btn-close-white ms-1" @click="clearFilter('nfdFrom')"></button>
                                </span>
                                <span v-if="filters.nfdTo" class="badge bg-info">
                                    NFD To: {{ filters.nfdTo }}
                                    <button class="btn-close btn-close-white ms-1" @click="clearFilter('nfdTo')"></button>
                                </span>
                                <span v-if="filters.dateFrom" class="badge bg-info">
                                    Created From: {{ filters.dateFrom }}
                                    <button class="btn-close btn-close-white ms-1" @click="clearFilter('dateFrom')"></button>
                                </span>
                                <span v-if="filters.sortBy" class="badge bg-secondary">
                                    Sort: {{ filters.sortBy }} ({{ filters.sortOrder }})
                                    <button class="btn-close btn-close-white ms-1" @click="clearFilter('sortBy')"></button>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="card-body">
                        <!-- KYT List -->
                        <div v-if="mainKyts.length > 0" class="table-responsive">
                            <table class="table table-hover">
                                <thead>
                                    <tr>
                                        <th scope="col">Main Log No</th>
                                        <th scope="col">Site Name</th>
                                        <th scope="col">Employee</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Related KYTs</th>
                                        <th scope="col">Category</th>
                                        <th scope="col">Sub Category</th>
                                        <th scope="col">Problem</th>
                                        <th scope="col">Solution</th>
                                        <th scope="col">Previous NFD</th>
                                        <th scope="col">Latest NFD</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="kyt in mainKyts" :key="kyt._id">
                                        <td>
                                            <button 
                                                type="button"
                                                class="btn btn-link p-0 text-primary fw-bold"
                                                @click="handleMainLogClick(kyt.parentMainLogNo)"
                                                :title="`Click to view details for ${kyt.parentMainLogNo}`"
                                            >
                                                {{ kyt.parentMainLogNo }}
                                            </button>
                                        </td>
                                        <td>
                                            <div class="fw-medium">{{ kyt.siteName }}</div>
                                        </td>
                                        <td>
                                            <div v-if="kyt.latestKytEmployee" class="small">
                                                <div class="fw-medium">{{ kyt.latestKytEmployee.employeeName }}</div>
                                                <div class="text-muted">{{ kyt.latestKytEmployee.employeeCode }}</div>
                                            </div>
                                            <span v-else class="text-muted small">No employee</span>
                                        </td>
                                        <td>
                                            <span 
                                                class="badge"
                                                :class="{
                                                    'bg-success': kyt.status === 'approved' || kyt.status === 'completed',
                                                    'bg-warning text-dark': kyt.status === 'pending' || kyt.status === 'in-progress',
                                                    'bg-danger': kyt.status === 'rejected' || kyt.status === 'cancelled',
                                                    'bg-info text-dark': kyt.status === 'open',
                                                    'bg-secondary': kyt.status === 'closed' || kyt.status === 'draft',
                                                    'bg-primary': kyt.status === 'active',
                                                    'bg-dark': !['approved', 'completed', 'pending', 'in-progress', 'rejected', 'cancelled', 'open', 'closed', 'draft', 'active'].includes(kyt.status)
                                                }"
                                            >
                                                {{ kyt.status.charAt(0).toUpperCase() + kyt.status.slice(1) }}
                                            </span>
                                        </td>
                                        <td>
                                            <div class="small">
                                                <div><strong>Total:</strong> {{ kyt.totalRelatedKyts }}</div>
                                                <div><strong>Children:</strong> {{ kyt.childKytsCount }}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div v-if="kyt.latestKytData?.length > 0" class="small">
                                                <div class="fw-medium">{{ kyt.latestKytData[0].category }}</div>
                                            </div>
                                            <span v-else class="text-muted small">No data</span>
                                        </td>
                                        <td>
                                            <div v-if="kyt.latestKytData?.length > 0" class="small">
                                                <div class="text-muted">{{ kyt.latestKytData[0].subCategory }}</div>
                                            </div>
                                            <span v-else class="text-muted small">No data</span>
                                        </td>
                                        <td>
                                            <div v-if="kyt.latestKytData?.length > 0" class="small">
                                                <div class="problem-text text-muted" :title="kyt.latestKytData[0].problem">
                                                    {{ truncateProblem(kyt.latestKytData[0].problem) }}
                                                </div>
                                            </div>
                                            <span v-else class="text-muted small">No problem</span>
                                        </td>
                                        <td>
                                            <div v-if="kyt.latestKytData?.length > 0" class="small">
                                                <div 
                                                    class="solution-text text-muted clickable" 
                                                    :title="kyt.latestKytData[0].solution"
                                                    @click="showFullSolution(kyt.latestKytData[0].solution)"
                                                >
                                                    {{ truncateSolution(kyt.latestKytData[0].solution) }}
                                                    <i v-if="isSolutionTruncated(kyt.latestKytData[0].solution)" 
                                                       class="fa fa-expand-alt ms-1 text-primary" 
                                                       title="Click to expand"></i>
                                                </div>
                                            </div>
                                            <span v-else class="text-muted small">No solution</span>
                                        </td>
                                        <td>
                                            <div v-if="kyt.previousKytNFD" class="small">
                                                {{ formatDate(kyt.previousKytNFD) }}
                                            </div>
                                            <span v-else class="text-muted small">N/A</span>
                                        </td>
                                        <td>
                                            <div class="small">
                                                {{ formatDate(kyt.latestKytNFD) }}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Empty State -->
                        <div v-else class="text-center py-5">
                            <i class="fa fa-inbox fa-3x text-muted mb-3"></i>
                            <h5 class="text-muted">No KYT logs found</h5>
                            <p class="text-muted">There are no KYT logs available at the moment.</p>
                        </div>

                        <!-- Pagination -->
                        <nav v-if="mainKytsData?.pagination && mainKytsData.pagination.totalPages > 1" 
                             aria-label="KYT logs pagination" class="mt-4">
                            <ul class="pagination justify-content-center">
                                <li class="page-item" :class="{ disabled: !mainKytsData.pagination.hasPrev }">
                                    <button 
                                        class="page-link" 
                                        @click="changePage(currentPage - 1)"
                                        :disabled="!mainKytsData.pagination.hasPrev"
                                    >
                                        Previous
                                    </button>
                                </li>
                                
                                <li 
                                    v-for="page in getPageNumbers()" 
                                    :key="page" 
                                    class="page-item" 
                                    :class="{ active: page === currentPage }"
                                >
                                    <button class="page-link" @click="changePage(page)">
                                        {{ page }}
                                    </button>
                                </li>
                                
                                <li class="page-item" :class="{ disabled: !mainKytsData.pagination.hasNext }">
                                    <button 
                                        class="page-link" 
                                        @click="changePage(currentPage + 1)"
                                        :disabled="!mainKytsData.pagination.hasNext"
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <!-- Full Solution Modal -->
        <div class="modal fade" id="fullSolutionModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            <i class="fa fa-lightbulb me-2 text-warning"></i>
                            Complete Solution
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div class="solution-container">
                            <pre class="solution-full-text">{{ fullSolutionText }}</pre>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                            <i class="fa fa-times me-1"></i>
                            Close
                        </button>
                        <button type="button" class="btn btn-primary" @click="copySolutionToClipboard">
                            <i class="fa fa-copy me-1"></i>
                            Copy to Clipboard
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { Modal } from 'bootstrap';
import kytChainService from '@/services/kytChainService';
import type { MainKytItem, MainKytsResponse } from '@/types/kyt-chain';

// Router
const router = useRouter();

// Reactive state
const loading = ref(false);
const error = ref('');
const mainKytsData = ref<MainKytsResponse | null>(null);
const currentPage = ref(1);
const pageSize = ref(10);
const showFilters = ref(true);
const fullSolutionText = ref('');

// Filters state
const filters = ref({
    siteName: '',
    empName: '',
    status: '',
    nfdFrom: '',
    nfdTo: '',
    dateFrom: '',
    sortBy: '',
    sortOrder: 'desc' as 'asc' | 'desc'
});

// Search debounce timer
let searchTimeout: NodeJS.Timeout | null = null;

// Computed properties
const mainKyts = computed(() => mainKytsData.value?.mainKyts || []);

const hasActiveFilters = computed(() => {
    return filters.value.siteName || 
           filters.value.empName || 
           filters.value.status || 
           filters.value.nfdFrom || 
           filters.value.nfdTo || 
           filters.value.dateFrom || 
           filters.value.sortBy;
});

// Methods
const fetchMainKyts = async (page: number = 1) => {
    loading.value = true;
    error.value = '';
    
    try {
        const queryFilters: any = {
            page,
            limit: pageSize.value
        };
        
        // Add active filters to query
        if (filters.value.siteName) queryFilters.siteName = filters.value.siteName;
        if (filters.value.empName) queryFilters.empName = filters.value.empName;
        if (filters.value.status) queryFilters.status = filters.value.status;
        if (filters.value.nfdFrom) queryFilters.nfdFrom = filters.value.nfdFrom;
        if (filters.value.nfdTo) queryFilters.nfdTo = filters.value.nfdTo;
        if (filters.value.dateFrom) queryFilters.dateFrom = filters.value.dateFrom;
        if (filters.value.sortBy) {
            queryFilters.sortBy = filters.value.sortBy;
            queryFilters.sortOrder = filters.value.sortOrder;
        }
        
        const response = await kytChainService.getMainKyts(queryFilters);
        
        if (response.success) {
            mainKytsData.value = response.data;
            currentPage.value = page;
        } else {
            throw new Error(response.message || 'Failed to fetch KYT logs');
        }
    } catch (err: any) {
        error.value = err.message || 'An error occurred while fetching KYT logs';
        console.error('Error fetching main KYTs:', err);
    } finally {
        loading.value = false;
    }
};

const handleMainLogClick = (parentMainLogNo: string) => {
    // Navigate to the KYTs by log page
    router.push(`/kyt-chain/logs/${parentMainLogNo}/kyts`);
};

const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch (err) {
        return 'Invalid Date';
    }
};

const truncateSolution = (solution: string): string => {
    if (!solution) return 'No solution';
    
    // Split solution into lines and take first 2-3 lines
    const lines = solution.split('\n').filter(line => line.trim() !== '');
    const maxLines = 3;
    const maxCharsPerLine = 60;
    
    let result = '';
    let lineCount = 0;
    
    for (const line of lines) {
        if (lineCount >= maxLines) break;
        
        if (line.length <= maxCharsPerLine) {
            result += (result ? '\n' : '') + line;
        } else {
            // Truncate long lines
            result += (result ? '\n' : '') + line.substring(0, maxCharsPerLine) + '...';
        }
        lineCount++;
    }
    
    // If there are more lines, add indication
    if (lines.length > maxLines) {
        result += '\n...';
    }
    
    return result;
};

const truncateProblem = (problem: string): string => {
    if (!problem) return 'No problem';
    
    // Split problem into lines and take first 2-3 lines
    const lines = problem.split('\n').filter(line => line.trim() !== '');
    const maxLines = 3;
    const maxCharsPerLine = 60;
    
    let result = '';
    let lineCount = 0;
    
    for (const line of lines) {
        if (lineCount >= maxLines) break;
        
        if (line.length <= maxCharsPerLine) {
            result += (result ? '\n' : '') + line;
        } else {
            // Truncate long lines
            result += (result ? '\n' : '') + line.substring(0, maxCharsPerLine) + '...';
        }
        lineCount++;
    }
    
    // If there are more lines, add indication
    if (lines.length > maxLines) {
        result += '\n...';
    }
    
    return result;
};

const isSolutionTruncated = (solution: string): boolean => {
    if (!solution) return false;
    
    const lines = solution.split('\n').filter(line => line.trim() !== '');
    const maxLines = 3;
    const maxCharsPerLine = 60;
    
    // Check if we have more than max lines
    if (lines.length > maxLines) return true;
    
    // Check if any line exceeds max characters
    for (const line of lines) {
        if (line.length > maxCharsPerLine) return true;
    }
    
    return false;
};

const showFullSolution = (solution: string) => {
    if (!solution) return;
    
    fullSolutionText.value = solution;
    
    const modalElement = document.getElementById('fullSolutionModal');
    if (modalElement) {
        const modal = new Modal(modalElement);
        modal.show();
    }
};

const copySolutionToClipboard = async () => {
    try {
        await navigator.clipboard.writeText(fullSolutionText.value);
        // You could add a toast notification here
        alert('Solution copied to clipboard!');
    } catch (err) {
        console.error('Failed to copy to clipboard:', err);
        alert('Failed to copy to clipboard. Please copy manually.');
    }
};

const changePage = (page: number) => {
    if (page >= 1 && mainKytsData.value?.pagination && page <= mainKytsData.value.pagination.totalPages) {
        fetchMainKyts(page);
    }
};

const getPageNumbers = (): number[] => {
    if (!mainKytsData.value?.pagination) return [];
    
    const totalPages = mainKytsData.value.pagination.totalPages;
    const current = currentPage.value;
    const pages: number[] = [];
    
    // Show up to 5 page numbers centered around current page
    const maxVisible = 5;
    let start = Math.max(1, current - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    // Adjust start if we're near the end
    if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }
    
    return pages;
};

// Filter methods
const applyFilters = () => {
    currentPage.value = 1; // Reset to first page when applying filters
    fetchMainKyts(1);
};

const debouncedSearch = () => {
    if (searchTimeout) {
        clearTimeout(searchTimeout);
    }
    
    searchTimeout = setTimeout(() => {
        applyFilters();
    }, 500); // 500ms delay for search
};

const clearFilters = () => {
    filters.value = {
        siteName: '',
        empName: '',
        status: '',
        nfdFrom: '',
        nfdTo: '',
        dateFrom: '',
        sortBy: '',
        sortOrder: 'desc'
    };
    applyFilters();
};

const clearFilter = (filterKey: string) => {
    if (filterKey === 'sortBy') {
        filters.value.sortBy = '';
        filters.value.sortOrder = 'desc';
    } else {
        (filters.value as any)[filterKey] = '';
    }
    applyFilters();
};

const toggleSortOrder = () => {
    filters.value.sortOrder = filters.value.sortOrder === 'asc' ? 'desc' : 'asc';
    if (filters.value.sortBy) {
        applyFilters();
    }
};

const toggleFilters = () => {
    showFilters.value = !showFilters.value;
};

// Lifecycle hooks
onMounted(() => {
    fetchMainKyts();
});
</script>

<style scoped>
.table th {
    background-color: #f8f9fa;
    font-weight: 600;
    border-top: none;
}

.table-hover tbody tr:hover {
    background-color: rgba(0, 123, 255, 0.075);
}

.btn-link {
    text-decoration: none;
}

.btn-link:hover {
    text-decoration: underline;
}

.badge {
    font-size: 0.75em;
}

.modal-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

.spinner-border {
    color: #0d6efd;
}

.btn-group .btn {
    margin-right: 2px;
}

.btn-group .btn:last-child {
    margin-right: 0;
}

.pagination .page-item.active .page-link {
    background-color: #0d6efd;
    border-color: #0d6efd;
}

.card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

.text-muted {
    color: #6c757d !important;
}

.fw-bold {
    font-weight: bold !important;
}

.fw-medium {
    font-weight: 500 !important;
}

.solution-text {
    white-space: pre-line;
    max-width: 200px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    line-height: 1.4;
}

.solution-text:hover {
    cursor: help;
}

.solution-text.clickable {
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.solution-text.clickable:hover {
    background-color: rgba(13, 110, 253, 0.1);
    border-radius: 4px;
    padding: 2px 4px;
}

.solution-container {
    max-height: 400px;
    overflow-y: auto;
}

.solution-full-text {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 1rem;
    margin: 0;
    white-space: pre-wrap;
    font-family: 'Courier New', monospace;
    font-size: 0.9rem;
    line-height: 1.5;
    color: #495057;
}

.modal-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

.modal-footer {
    background-color: #f8f9fa;
    border-top: 1px solid #dee2e6;
}

.problem-text {
    white-space: pre-line;
    max-width: 200px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    line-height: 1.4;
}

.problem-text:hover {
    cursor: help;
}

.bg-light {
    background-color: #f8f9fa !important;
    color: #212529 !important;
}

.card-body.bg-light {
    background-color: #f8f9fa !important;
}

.card-body.bg-light .form-label {
    color: #212529 !important;
    font-weight: 600 !important;
}

.form-label.small {
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
    color: #495057 !important;
    font-weight: 600 !important;
}

.form-label.small.fw-medium {
    color: #212529 !important;
    font-weight: 600 !important;
}

.badge .btn-close {
    font-size: 0.65em;
    margin-left: 0.25rem;
}

.badge .btn-close-white {
    filter: invert(1) grayscale(100%) brightness(200%);
}

.input-group-sm .btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
}

.form-control-sm, .form-select-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
}

.border-bottom {
    border-bottom: 1px solid #dee2e6 !important;
}

/* Active filters section */
.card-body.bg-light .small.text-muted {
    color: #6c757d !important;
    font-weight: 500;
}

/* Ensure all text in filter section is visible */
.card-body.bg-light * {
    color: inherit;
}

.card-body.bg-light .form-control,
.card-body.bg-light .form-select {
    color: #212529 !important;
    background-color: #fff !important;
}
</style>
